import type { BusinessCalendar } from "@helpdesk/sla";

import { ActivityLink } from "../ActivityDrawer";
import { Avatar } from "../ui/Avatar";
import { Badge } from "../ui/Badge";
import { ChannelIcon } from "../ui/ChannelIcon";
import { InlineSelect } from "./InlineSelect";
import { listGrid } from "./TicketListPane";
import { cn } from "../ui/cn";
import { CopyButton } from "../CopyButton";
import { SlaIndicator } from "../SlaClock";
import { assigneeOf } from "../../lib/agents";
import { useLiveSla } from "../../lib/sla";
import { shortDateTime } from "../../lib/time";
import type { Pipeline, TicketSummary, User } from "../../types";

interface TicketRowProps {
  ticket: TicketSummary;
  pipelines: Pipeline[];
  users: User[];
  calendar: BusinessCalendar;
  selected: boolean;
  /** Customer mail on this ticket the signed-in agent has not seen yet. */
  unread: boolean;
  onSelect: (id: number) => void;
  onAssigneeChange: (ticketId: number, assigneeId: number | null) => void;
  onPipelineChange: (ticketId: number, pipelineId: number | null) => void;

  /** Managers only: a tick-box for acting on several tickets at once. */
  selectable?: boolean;
  checked?: boolean;
  onToggle?: (id: number) => void;
}



/**
 * One row of the ticket list.
 *
 * Rendered as a grid rather than a table so the whole row can be a single click
 * target while the email address and ticket id stay independently clickable for
 * copying. The column template is shared with the header in TicketListPane.
 */
export function TicketRow({
  ticket,
  pipelines,
  users,
  calendar,
  selected,
  unread,
  onSelect,
  onAssigneeChange,
  onPipelineChange,
  selectable = false,
  checked = false,
  onToggle,
}: TicketRowProps) {
  const sla = useLiveSla(ticket, pipelines, calendar);
  const pipeline = pipelines.find((entry) => entry.id === ticket.pipelineId);

  return (
    <div
      role="row"
      tabIndex={0}
      onClick={() => onSelect(ticket.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(ticket.id);
        }
      }}
      className={cn(
        listGrid(selectable),
        "py-1.5 text-sm",
        "cursor-pointer transition-colors focus:outline-none",
        /*
         * Banded rather than ruled, matching the companies list. A queue is
         * eight columns wide and the eye loses its place across it; a filled
         * band carries it further than a hairline does, and it *is* the
         * separator -- so the row border came off rather than being drawn on
         * top of it.
         *
         * Only on the rows that are not picked out for another reason.
         * `odd:`/`even:` are nth-child selectors and so outrank a plain
         * `bg-*`, which would have let the stripe paint over the selection.
         */
        checked
          ? "bg-brand-50/60 dark:bg-brand-950/30"
          : selected
            ? "bg-brand-50 dark:bg-brand-950/40"
            : "odd:bg-surface even:bg-app hover:bg-surface-2",
      )}
    >
      {selectable ? (
        // Stopped from reaching the row, which would open the ticket -- the
        // whole row is a click target, and ticking a box is not asking to
        // leave the list.
        <input
          type="checkbox"
          checked={checked}
          onClick={(event) => event.stopPropagation()}
          onChange={() => onToggle?.(ticket.id)}
          aria-label={`Select ticket ${ticket.id}`}
          className="size-3.5 accent-brand-600"
        />
      ) : null}

      {/*
        Date of the last email. Bold across the whole row when there is
        customer mail on it you have not opened -- the same signal every mail
        client uses, and the one an agent scans a queue for. Read state is per
        agent, so a colleague reading it does not clear it for you.
      */}
      <div
        className={cn(
          "truncate text-xs tabular-nums",
          unread ? "font-semibold text-content" : "text-muted",
        )}
      >
        {shortDateTime(ticket.lastMessageAt)}
      </div>

      {/*
        How it reached us, immediately left of who it is from: the sentence it
        completes runs "on WhatsApp, from this company, about this", and the
        glyph is the first thing on the row that is worth noticing when it is
        not the envelope. Further right it became a fact you look up rather
        than one you read on the way past, and it cost a text column its width
        to say so -- the column is now exactly the icon.
      */}
      <div>
        <ChannelIcon type={ticket.channelType} name={ticket.channelName} />
      </div>

      {/*
        Who it is from. Company leads because it is what agents recognise;
        the contact name and address share the second line so all three fit
        without a third row.

        Both names open their history in the drawer. That is the lookup an
        agent makes while working down a queue -- "have we spoken to these
        people before" -- and it should not cost leaving the queue to make it.
      */}
      <div className="min-w-0">
        <div className={cn("truncate text-content", unread && "font-semibold")}>
          {ticket.companyName ? (
            <ActivityLink
              kind="company"
              id={ticket.companyId}
              name={ticket.companyName}
            />
          ) : (
            <ActivityLink
              kind="contact"
              id={ticket.contactId}
              name={ticket.contactName}
            >
              {ticket.contactName ?? "Unknown"}
            </ActivityLink>
          )}
        </div>
        <div className="flex min-w-0 items-baseline gap-1 text-xs text-muted">
          {ticket.companyName && ticket.contactName ? (
            <span className="shrink-0 truncate">
              <ActivityLink
                kind="contact"
                id={ticket.contactId}
                name={ticket.contactName}
              />
            </span>
          ) : null}
          {ticket.contactEmail ? (
            <CopyButton
              value={ticket.contactEmail}
              what="email address"
              className="min-w-0"
            />
          ) : null}
        </div>
      </div>

      {/*
        Subject only -- the message preview is deliberately absent. It doubled
        every row's height for text that is truncated to uselessness at this
        width, and the thread is one click away.

        `title` because this column is narrow and a real subject is often long
        enough that the first few words are all that survive: "RE: FW: order" is
        three tickets, not one. The tooltip is a stopgap for a hover -- the
        readable answer is the ticket header, which wraps.
      */}
      <div
        title={ticket.subject}
        className={cn("min-w-0 truncate text-content", unread && "font-semibold")}
      >
        {ticket.subject}
      </div>

      {/* Ticket id */}
      <div className="text-xs text-muted tabular-nums">
        <CopyButton
          value={String(ticket.id)}
          label={`#${ticket.id}`}
          what="ticket ID"
        />
      </div>

      {/* Assignee. Editable in place -- reassigning is the commonest thing
          done while working down a queue, and it should not need the ticket
          to be opened first. */}
      <InlineSelect
        label={`Assignee for ticket ${ticket.id}`}
        value={ticket.assigneeId === null ? "" : String(ticket.assigneeId)}
        onChange={(raw) =>
          onAssigneeChange(ticket.id, raw === "" ? null : Number(raw))
        }
        options={[
          { value: "", label: "Unassigned" },
          ...users.map((user) => ({
            value: String(user.id),
            label: user.name,
          })),
        ]}
      >
        <Avatar
          agent={assigneeOf(users, ticket.assigneeId, ticket.assigneeName)}
        />
      </InlineSelect>

      {/* Time to SLA */}
      <div>
        <SlaIndicator sla={sla} />
      </div>

      {/* Pipeline. Also editable here: triage is exactly the moment an
          unclassified ticket gets its pipeline, and therefore its SLA. */}
      <InlineSelect
        label={`Pipeline for ticket ${ticket.id}`}
        value={ticket.pipelineId === null ? "" : String(ticket.pipelineId)}
        onChange={(raw) =>
          onPipelineChange(ticket.id, raw === "" ? null : Number(raw))
        }
        options={[
          { value: "", label: "No pipeline" },
          ...pipelines.map((entry) => ({
            value: String(entry.id),
            label: entry.name,
          })),
        ]}
      >
        {pipeline ? (
          <Badge color={pipeline.color} className="max-w-full">
            <span className="truncate">{pipeline.name}</span>
          </Badge>
        ) : (
          <span className="text-xs text-faint">—</span>
        )}
      </InlineSelect>

    </div>
  );
}
